import React from 'react';
import { Link } from 'react-router-dom';
import { SERVICE_FEE_RATE, SERVICE_FEE_MIN_USD } from '../config/pricing';

const STEPS = [
  { icon: '🔗', title: 'Send a link', desc: 'Paste the link of any item sold in Korea — shops, brand sites, or marketplaces.' },
  { icon: '🧾', title: 'Get a quote', desc: 'We check availability and send you the item price, service fee, and shipping.' },
  { icon: '💳', title: 'Pay securely', desc: 'Approve the quote and pay with PayPal. Nothing is bought before you approve.' },
  { icon: '📦', title: 'We ship to you', desc: 'We buy it, film the packing, and ship from Korea with a tracking number.' },
];

const FAQS = [
  {
    q: 'What exactly do you do?',
    a: 'You send us a link to something sold in Korea. We buy it on your behalf, check it, pack it, and ship it to your address abroad. You do not need a Korean address, phone number, or card.',
  },
  {
    q: 'How much does it cost?',
    a: `The item price, a service fee of ${SERVICE_FEE_RATE * 100}% of the item price with a minimum of $${SERVICE_FEE_MIN_USD}, and shipping. You see all three broken out in your quote before you pay anything. Customs duties, taxes, and import fees charged by your own country are not included and are your responsibility.`,
  },
  {
    q: 'When am I charged?',
    a: 'Only after you approve the quote. We do not buy the item, and you are not charged, until you press pay. Submitting a request costs nothing.',
  },
  {
    q: 'What if the item costs more than the quote?',
    a: 'If the actual cost turns out higher — a price change or heavier-than-expected shipping — we send you an additional charge with the reason. You review it and pay it before we ship. Nothing is charged silently.',
  },
  {
    q: 'What if the item is sold out or cannot be bought?',
    a: 'We reject the request and tell you why. Since payment only happens after you approve a quote, there is nothing to refund — you are never charged for a request we could not fulfill.',
  },
  {
    q: 'Can I cancel?',
    a: 'Yes, while your request is still awaiting a quote or awaiting payment — use the Cancel button on the request. Once we have already bought the item on your behalf, cancellation may not be possible.',
  },
  {
    q: 'How do I know it actually shipped?',
    a: 'We film the packing and share the video with you, along with a tracking number, when the request moves to shipped.',
  },
  {
    q: 'How long does delivery take?',
    a: 'It depends on the seller, the destination country, and customs. Delivery times are estimates and vary — we give you a tracking number so you can follow the parcel from Korea to your door.',
  },
  {
    q: 'What if it arrives damaged?',
    a: 'Email us at support@moashops.com with a description and, if possible, photos of the damage and the packaging. We also recommend filing an inquiry with the carrier. As an intermediary we cannot guarantee a specific outcome, but we will work through it with you.',
  },
];

const FaqPage: React.FC = () => (
  <div style={s.page}>
    <h1 style={s.title}>How It Works</h1>
    <p style={s.sub}>Buying from Korea, without a Korean address or card.</p>

    <div style={s.steps}>
      {STEPS.map((step, i) => (
        <div key={step.title} style={s.step}>
          <span style={s.stepNum}>{i + 1}</span>
          <span style={s.stepIcon}>{step.icon}</span>
          <h3 style={s.stepTitle}>{step.title}</h3>
          <p style={s.stepDesc}>{step.desc}</p>
        </div>
      ))}
    </div>

    <div style={s.ctaWrap}>
      <Link to="/purchase-requests" style={s.cta}>Request an item →</Link>
    </div>

    <h2 style={s.faqHeading}>Frequently asked</h2>
    <div style={s.faqList}>
      {FAQS.map(item => (
        <div key={item.q} style={s.faqItem}>
          <h3 style={s.q}>{item.q}</h3>
          <p style={s.a}>{item.a}</p>
        </div>
      ))}
    </div>

    <p style={s.footNote}>
      Still unsure? <Link to="/support" style={s.footLink}>Contact us</Link> — or read the{' '}
      <Link to="/terms" style={s.footLink}>Terms of Service</Link>.
    </p>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 820, margin: '56px auto', padding: '0 20px 100px' },
  title: { fontSize: 30, fontWeight: 800, color: '#333', marginBottom: 8 },
  sub: { fontSize: 15, color: '#999', marginBottom: 44 },
  steps: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 },
  step: { position: 'relative', background: '#faf7fe', borderRadius: 14, padding: '28px 18px 22px', textAlign: 'center' },
  stepNum: { position: 'absolute', top: 12, left: 14, fontSize: 12, fontWeight: 800, color: '#c9b6e6' },
  stepIcon: { fontSize: 32, display: 'block', marginBottom: 10 },
  stepTitle: { fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 6 },
  stepDesc: { fontSize: 12.5, color: '#888', lineHeight: 1.6 },
  ctaWrap: { textAlign: 'center', margin: '36px 0 60px' },
  cta: { display: 'inline-block', padding: '14px 32px', background: '#b39ddb', color: '#fff', borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: 'none' },
  faqHeading: { fontSize: 20, fontWeight: 800, color: '#333', marginBottom: 20 },
  faqList: { display: 'flex', flexDirection: 'column', gap: 4 },
  faqItem: { padding: '20px 0', borderBottom: '1px solid #f2f2f2' },
  q: { fontSize: 15.5, fontWeight: 700, color: '#333', marginBottom: 8 },
  a: { fontSize: 14, color: '#777', lineHeight: 1.75 },
  footNote: { marginTop: 40, fontSize: 13.5, color: '#aaa' },
  footLink: { color: '#b39ddb', fontWeight: 600, textDecoration: 'none' },
};

export default FaqPage;
